<template>
  <modal name="alarm-auto-test" class="modal-inner modal-update-addon" transition="pop-out" :width="modalWidth" :focus-trap="true" :min-height="600" height="auto" :scrollable="true" :resizable="true" :adaptive="true" :reset="true" @before-close="beforeClose">
    <LoadingSpinner :isLoading="isLoading" v-if="isLoading" />
    <span class="close-button" @click="hide('alarm-auto-test')"><BaseIcon icon="times-circle" class="text-white"/></span>
    <div class="modal-header text-lg bg-primary-50 font-serif">{{ $t("alarm_auto_test.test_device") }} - {{ alarmInfo.unit_name }}</div>
    <div class="modal-body">
      <ValidationObserver ref="AlarmAutoTest">
        <div class="p-4">
          <div class="bg-red-100 px-2 py-1 rounded-md mb-3 text-gray-600 text-xxs italic" v-if="showErrorMessage">{{ $t("alarm_auto_test.error_reset") }}</div>
          <div class="bg-red-100 px-2 py-1 rounded-md mb-3 text-gray-600 text-xxs italic" v-if="connectionLost && step === 2">{{ $t("alarm_auto_test.connection_lost") }}</div>
          <p v-if="stepCounterText" class="text-xs text-gray-500 mb-3">{{ stepCounterText }}</p>
          <div v-if="step === 1" class="">
            <p v-if="previousTestNote" class="bg-gray-100 px-2 py-1 rounded-md mb-3 text-gray-600 text-xs">{{ previousTestNote }}</p>
            <p class="text-lg font-bold text-red-500">{{ $t("alarm_auto_test.test_mode_alert") }}</p>
            <p class="font-semibold mt-2">{{ $t("alarm_auto_test.alarm_auto_test") }}</p>
            <p class="mt-4">{{ $t("alarm_auto_test.alarm_phone_message") }}</p>
            <div class="mt-4">
              <ValidationProvider :name="$t('alarm_auto_test.alarm_phone')" rules="required" v-slot="{ classes, errors }">
                <div class="input-validate" :class="classes">
                  <input 
                    type="tel" 
                    v-model="alarm_mobile" 
                    :placeholder="countryPrefix + '766999222'" 
                    class="w-full bg-gray-100 rounded text-sm text-gray-900 focus:outline-none border-b border-gray-300 transition duration-500 px-3 py-1" 
                    @keypress="onlyNumbers"
                    maxlength="20"
                  />
                  <span>{{ errors[0] }}</span>
                </div>
              </ValidationProvider>
            </div>
          </div>
          <div v-if="step === 2">
            <p class="text-center text-4xl text-gray-400">
              <BaseIcon icon="hand-point-down" class="text-red-500 ml-1 animate-bounce" v-if="testModeStatus == 'device_test_config'" />
              <BaseIcon icon="exclamation-triangle" class="text-red-500 ml-1" v-else-if="testModeStatus == 'error' || testStoppedPageLost || testStoppedWhileRestoring" />
              <BaseIcon icon="check-circle" class="text-green-500 ml-1" v-else-if="testModeStatus == 'done' && testEventRecieved" />
              <BaseIcon icon="info-circle" class="text-gray-400 ml-1" v-else-if="testModeStatus == 'done'" />
              <BaseIcon icon="clock" spin class="ml-1" v-else-if="testModeStatus != ''" />
            </p>
            <p class="text-center italic text-sm font-serif mt-1" :class="{ 'text-red-500  animate-bounce': testModeStatus == 'device_test_config', 'text-red-500 font-bold': testHasStopped || testStoppedPageLost, 'font-bold': testEndedWithoutPress }">
              {{ testModeStatusMessage }}
            </p>
            <p v-if="isTestGettingReady" class="text-center text-sm font-bold mt-2 text-red-500">{{ $t("alarm_auto_test.do_not_press_yet") }}</p>
            <p v-if="stopReasonMessage" class="text-center text-sm mt-2 text-gray-600">{{ stopReasonMessage }}</p>
            <p v-if="waitHintMessage" class="text-center text-xs mt-1 text-gray-500">{{ waitHintMessage }}</p>
            <hr class="mt-3">
            <p class="text-center text-xs mt-4">
              <span v-if="testEventRecieved" class=" text-green-400"><BaseIcon icon="check" class="mr-1" />{{ $t("alarm_auto_test.success_message") }}</span>
              <span v-else class=" text-red-400"><BaseIcon icon="times" class="mr-1" />{{ $t("alarm_auto_test.no_push_button") }}</span>
            </p>
            <p class="text-center text-xxs mt-3 mb-1 italic text-gray-300">{{ alarmWillCall ? $t("alarm_auto_test.test_mobile_message") : $t("alarm_auto_test.test_mobile_message_sms") }} {{ alarm_mobile }}</p>
            <hr class="mt-3">
            <p v-if="timeLeft && testModeStatus != 'done' && testModeStatus != 'error'" class="text-center text-xs mt-3 text-gray-500">
              {{ $t("alarm_auto_test.remaining_time") }} <b class="text-sm my-1">{{ timeLeft }}</b><br>
              <!-- After the press the countdown already runs to the end of the test. -->
              <template v-if="testModeStatus != 'recieved_event'">{{ $t("alarm_auto_test.remaining_time_success") }}</template>
            </p>
          </div>
        </div>
      </ValidationObserver>
    </div>
    <div class="modal-footer text-right p-3">
      <button v-if="step === 1" class="btn-blue-outline" @click="activateAlarmTesting()">
        <span style="font-size: 16px;">{{ $t("alarm_auto_test.active") }}</span>
      </button>
      <button v-if="canStartNewTest" class="btn-blue-outline" @click="startOver()">{{ $t("alarm_auto_test.start_new_test") }}</button>
      <button v-if="step !== 1 && testModeStatus != 'done' && !testHasStopped" class="btn-red" @click="resetAlarmTesting()">{{ $t("user_actions.deactivate") }}</button>
    </div>
  </modal>
</template>

<script>
import { VIRTUAL_DEVICE_NO_TEST } from "@/utils/virtualDevice";

const MODAL_WIDTH = 600;

export default {
  props: {},
  data() {
    return {
      step: 1,
      testingIsActive: false,
      alarm_mobile: null,
      alarmInfo: { name: "" },
      device: null,
      gps: null,
      testModeStatus: '',
      testEventRecieved: false,
      showErrorMessage: false,
      timer: null,
      timeLeft: "",
      defaultDuration: 1200,
      duration: 20 * 60,
      intervalId: null, // To store the interval ID
      isLoading: false,
      // Status checks that failed in a row, and whether the page says so.
      failedStatusChecks: 0,
      connectionLost: false,
      // The user answered "Yes, stop the test" when closing the window.
      stopConfirmed: false,
      // Why the latest test stopped early. The TCP server empties the test when it
      // has put the alarm back, so the reason is remembered here for the last screen.
      lastFailReason: '',
      // The run id of the test this window follows, to read its saved result.
      watchedRunId: '',
      // A status check is waiting for its answer.
      statusCheckRunning: false,
      // When the alarm started getting ready, and whether it takes longer than usual.
      gettingReadySince: null,
      alarmIsSlow: false,
      // The test whose result this window has already shown, so opening the window
      // again doesn't repeat it as "the latest test".
      shownResultRunId: '',
    };
  },

  components: {},

  methods: {
    onlyNumbers(event){
      const char = String.fromCharCode(event.which);
      if (!/[0-9+]/.test(char)) {
        event.preventDefault();
      }
    },

    setAlarmInfo(alarmInfo) {
      this.step = 1;
      this.alarmInfo = alarmInfo;

      this.alarm_mobile = this.alarmInfo.test_sms_number;

      // Get current time and previously stored values
      const now = this.moment.utc();
      const previousImei = this.alarmInfo.imei_number;
      const previousTime = this.alarmInfo.last_tunnel_disable;
      const lastTunnelDisableTimestamp = this.moment.utc(previousTime);

      // Calculate time difference in seconds
      const testingPeriodInSeconds = now.diff(lastTunnelDisableTimestamp, "seconds");

      // Check if the current IMEI matches the previous IMEI and if the testing period is within 20 minutes
      if (this.alarmInfo.imei_number === previousImei && testingPeriodInSeconds < this.defaultDuration) {
        // If testing is not active, initiate countdown
        if (!this.timeLeft) {
          this.duration = this.defaultDuration - testingPeriodInSeconds;
          this.startCountdown();
        }
        // Set step and testing status after ensuring UI updates
        this.$nextTick(() => {
          this.step = 2;
          this.testingIsActive = true;
        });
      } else {
        // Reset duration if conditions are not met
        this.duration = this.defaultDuration;
      }

      // Always check the alarm testing status at the end
      this.getAlarmTestingStatus();
    },

    async activateAlarmTesting() {
      this.$refs.AlarmAutoTest.validate().then(async (success) => {
        if (!success) {
          return;
        }
        this.testingIsActive = true;
        this.testEventRecieved = false;
        this.showErrorMessage = false;
        this.isLoading = true;
        this.gettingReadySince = null;
        this.alarmIsSlow = false;

        try {
          let apiUrl = `${process.env.VUE_APP_SERVER_URL}/device-event-tunneling/disable/${this.alarmInfo.imei_number}`;
          let response = await this.axios.post(apiUrl, { notificationPhone: this.alarm_mobile });

          if (response.data.success) {
            this.device = response.data.device;
            this.gps = response.data.gps;
            this.lastFailReason = '';
            this.watchedRunId = this.device.test_mode ? this.device.test_mode.run_id : '';
            this.step = 2;
            this.startCountdown();
            this.isLoading = false;

            this.$emit("updateAlarm");
          }
          else {
            // The server answered but did not start the test. Tell the operator,
            // instead of leaving a screen that looks exactly like before.
            this.showActivationError(response.data.code);
          }
        } catch (error) {
          this.showActivationError(this.errorCode(error), this.secondsToWait(error));
        }
      });
    },

    // Pull the reason code out of a failed request, if the server sent one.
    errorCode(error) {
      if (error && error.response && error.response.data) return error.response.data.code;
      return null;
    },

    // How long the server says to wait before a new test, if it sent it.
    secondsToWait(error) {
      if (error && error.response && error.response.data) return error.response.data.seconds_to_wait;
      return null;
    },

    // For example "1 min 30 s". Rounded up to 10 seconds, so a customer who waits
    // exactly that long is not turned away again.
    waitTimeText(seconds) {
      const roundedSeconds = Math.ceil(seconds / 10) * 10;
      const minutes = Math.floor(roundedSeconds / 60);
      const restSeconds = roundedSeconds % 60;

      if (minutes === 0) return `${restSeconds} s`;
      if (restSeconds === 0) return `${minutes} min`;
      return `${minutes} min ${restSeconds} s`;
    },

    // Why a test stopped, in the customer's words ('' when there is nothing to add).
    failReasonText(reason) {
      if (reason == 'offline') return this.$t('alarm_auto_test.reason_offline');
      if (reason == 'no_answer' || reason == 'not_ready_in_time') return this.$t('alarm_auto_test.reason_no_answer');
      if (reason == 'rejected') return this.$t('alarm_auto_test.reason_rejected');
      if (reason == 'no_press') return this.$t('alarm_auto_test.reason_no_press');
      if (reason == 'cancelled') return this.$t('alarm_auto_test.reason_cancelled');
      if (reason == 'restore_not_confirmed') {
        return this.$t('alarm_auto_test.reason_restore_not_confirmed', { button: this.$t('user_actions.deactivate') });
      }
      // The check of the alarm's own set-up refused the test before anything was changed.
      if (reason == 'routes_elsewhere') return this.$t('alarm_auto_test.reason_routes_elsewhere');
      if (reason == 'press_not_recognised') return this.$t('alarm_auto_test.reason_press_not_recognised');
      if (reason == 'not_routed_to_securcloud') return this.$t('alarm_auto_test.reason_not_routed_to_securcloud');
      if (reason == 'button_disabled') return this.$t('alarm_auto_test.reason_button_disabled');
      if (reason == 'config_not_read') return this.$t('alarm_auto_test.reason_config_not_read');
      return '';
    },

    // Show why the test could not be started. Without this the dialog silently
    // stayed on the activate button, so the operator pressed it again - and a
    // second activation throws away the device's saved settings.
    showActivationError(code, secondsToWait) {
      this.isLoading = false;
      this.testingIsActive = false;

      let message = this.$t("alarm_auto_test.activation_failed");

      if (code === "device_offline") {
        message = this.$t("alarm_auto_test.activation_device_offline");
      }
      else if (code === "model_not_supported") {
        message = this.$t("alarm_auto_test.model_not_supported");
      }
      else if (code === "recent_press") {
        message = this.$t("alarm_auto_test.activation_recent_press");
        // Say how long, so the customer isn't turned away again after a minute.
        if (secondsToWait > 0) {
          message = this.$t("alarm_auto_test.activation_recent_press_wait", { time: this.waitTimeText(secondsToWait) });
        }
      }
      else if (code === "invalid_phone") {
        message = this.$t("alarm_auto_test.activation_invalid_phone");
      }
      else if (code === "test_needs_attention") {
        message = this.$t("alarm_auto_test.test_needs_attention");
        this.getAlarmTestingStatus();
      }
      else if (code === "test_already_running") {
        message = this.$t("alarm_auto_test.activation_already_running");
        // Someone else started this test. Forget the number typed here, so the
        // window shows the number the alarm really calls (customer test, L1).
        this.alarm_mobile = null;
        // A test really is running, so show that state instead of the activate button.
        this.getAlarmTestingStatus();
      }
      else if (code === VIRTUAL_DEVICE_NO_TEST) {
        // An app user's device: there is no hardware behind it to put in test
        // mode, and securcloud refuses before writing anything (409, see
        // utils/virtualDevice.js). "Try again" would be the wrong advice.
        message = this.$t("alarm_info.virtual_device_no_test");
      }

      this.handleError(null, message);
    },

    async resetAlarmTesting() {
      try {
        let apiUrl = `${process.env.VUE_APP_SERVER_URL}/device-event-tunneling/reset/${this.alarmInfo.imei_number}`;
        let response = await this.axios.get(apiUrl);

        if (response.data.success) {
          this.device = response.data.device;
          this.gps = response.data.gps;

          this.$emit("updateAlarm");
          return true;
        }
        return false;
      } catch (error) {
        // A failed restore means the device may still be in test configuration.
        // That must never look like a clean deactivation.
        let message = this.errorCode(error) === "restore_failed"
          ? this.$t("alarm_auto_test.restore_failed")
          : this.$t("alarm_auto_test.error_reset");

        this.showErrorMessage = true;
        this.handleError(null, message);
        return false;
      }
    },
    
    async getAlarmTestingStatus() {
      // Skip this check while the previous one still waits for its answer, so
      // answers can't arrive out of order (SEC-407).
      if (this.statusCheckRunning) return;
      this.statusCheckRunning = true;

      try {
        let apiUrl = `${process.env.VUE_APP_SERVER_URL}/device-event-tunneling/status/${this.alarmInfo.imei_number}`;
        // A time limit, so a check on a dead connection fails instead of hanging.
        let response = await this.axios.get(apiUrl, { timeout: 8000 });

        // The connection works (again).
        this.failedStatusChecks = 0;
        this.connectionLost = false;

        if (response.data.success) {
          this.device = response.data.device;
          this.gps = response.data.gps;

          const runningStatuses = ['tunnel_disable', 'device_query_init', 'device_query_success', 'device_test_config', 'recieved_event', 'tunnel_enable'];

          // The logged-out page opens this window without the tester's number.
          // Take it from the server, so both pages show it the same way (SEC-407).
          // A running test keeps the number in test_mode until "press". Only while
          // a test runs: an ended test's number belongs to whoever ran it.
          const testIsRunning = runningStatuses.includes(this.device.test_mode.status);
          const serverTesterNumber = this.device.test_mode.tester_number || this.device.test_sms_number;
          if (!this.alarm_mobile && testIsRunning && serverTesterNumber) this.alarm_mobile = serverTesterNumber;

          // The test this window follows. How it ended is saved outside test_mode
          // (test_result), because the TCP server empties test_mode at the end.
          if (this.device.test_mode.run_id) this.watchedRunId = this.device.test_mode.run_id;
          const lastResult = this.device.test_result;
          const resultIsForThisTest = Boolean(lastResult && lastResult.run_id && lastResult.run_id === this.watchedRunId);

          if (this.device.test_mode.fail_reason) this.lastFailReason = this.device.test_mode.fail_reason;
          if (resultIsForThisTest && lastResult.fail_reason) this.lastFailReason = lastResult.fail_reason;

          // A test that ends goes back to an empty status. Show it as finished, not
          // as the empty start form, also when this page missed the steps in
          // between (for example a phone with its screen off) (SEC-407).
          const previousStatus = this.testModeStatus;
          const wasFollowingATest = runningStatuses.includes(previousStatus) || previousStatus == 'done';
          const testJustEnded = this.device.test_mode.status == '' && wasFollowingATest;
          if (testJustEnded) this.testModeStatus = 'done';
          else this.testModeStatus = this.device.test_mode.status;

          // An alarm with weak coverage can take over a minute to get ready. After
          // 30 seconds the page says so, instead of a hint the alarm can't keep.
          const gettingReadyStatuses = ['tunnel_disable', 'device_query_init', 'device_query_success'];
          const alarmIsGettingReady = gettingReadyStatuses.includes(this.testModeStatus);
          if (!alarmIsGettingReady) this.gettingReadySince = null;
          else if (!this.gettingReadySince) this.gettingReadySince = Date.now();
          this.alarmIsSlow = alarmIsGettingReady && Date.now() - this.gettingReadySince > 30000;

          // The countdown shows the time left in the current step, by the server's clock.
          this.followServerCountdown(response.data.seconds_left);

          let now = this.moment.utc(); // Represents the current time in UTC
          let lastTunnelDisableTimestamp = this.device.last_tunnel_disable;
          let testingPeriod = now.diff(lastTunnelDisableTimestamp, "seconds");

          //update last_tunnel_disable timestamp in the auto-provlarm standalone dashboard :: start
          let alarmFromStore = JSON.parse(JSON.stringify(this.$store.getters.getAutoProvlarm)); //this.$store.getters.getAutoProvlarm;
          if (alarmFromStore !== null) {
            alarmFromStore.device.device_last_test_time = this.device.last_tunnel_disable;
            this.$store.dispatch("autoProvlarm", alarmFromStore);
          }
          //update last_tunnel_disable timestamp in the auto-provlarm standalone dashboard :: stop

          // set relevant screen
          // '' = test mode is inactive
          if (this.testModeStatus == '') {
            this.testingIsActive = false;
            this.step = 1;
          }
          else if(testingPeriod <= this.defaultDuration || this.testRunNeedsWindow) {
            this.testingIsActive = true;
            this.step = 2;
          }
          // todo: ?when timer expires

          // set push-button received event status
          // Only the server's "press received" counts. A press before "press"
          // appeared is not the test press, so the press time alone could show a
          // success that never happened (SEC-407).
          if (this.testModeStatus == 'recieved_event') {
            this.testEventRecieved = true;
          }
          // The server notes the press too, so it counts even when this page missed it.
          if (resultIsForThisTest && lastResult.pressed_at) {
            this.testEventRecieved = true;
          }

          // in case deactivation is not successful
          // (a test that stopped before the alarm was changed has nothing to deactivate)
          if (this.testModeStatus == 'error' && !this.testStoppedBeforeChange) this.showErrorMessage = true;
          else if (this.testModeStatus == 'tunnel_enable' && !this.testingIsActive && !this.testStoppedWhileRestoring) this.showErrorMessage = true;
          else this.showErrorMessage = false;

        }
      } catch (error) {
        // During a test a failed check is usually a short network drop. Show one
        // calm notice after three failures instead of an error pop-up every 5
        // seconds, and keep trying.
        if (this.step === 2) {
          this.failedStatusChecks++;
          if (this.failedStatusChecks >= 3) this.connectionLost = true;
          return;
        }

        this.handleError(error);
      } finally {
        this.statusCheckRunning = false;
      }
    },

    // Before, the page counted down its own 20 minutes and never heard about the
    // server's time limits (PM decision D5). Now each status check restarts the
    // countdown from the seconds the server says are left in the current step.
    followServerCountdown(secondsLeft) {
      // An older server sends nothing: keep the page's own countdown.
      if (secondsLeft === undefined) return;

      // Nothing to count down, for example while the alarm is being put back, or
      // the time is up and the server ends the step at its next check. Restarting
      // the countdown at 0 on every status check made the line flicker.
      if (secondsLeft === null || secondsLeft <= 0) {
        if (this.timer) {
          clearInterval(this.timer);
          this.timer = null;
        }
        this.timeLeft = null;
        return;
      }

      this.duration = secondsLeft;
      this.startCountdown();
    },

    // Closing the window while the test is getting ready or waiting for the
    // press asks first (SEC-407). Before, the test went on with nobody watching.
    beforeClose(event) {
      if (this.isTestWaitingForUser && !this.stopConfirmed) {
        event.stop();
        this.askToStopTest();
        return;
      }

      this.stopConfirmed = false;
      this.resetAllTimers();
    },

    askToStopTest() {
      this.$modal.show("dialog", {
        title: this.$t("alarm_auto_test.stop_test_question"),
        buttons: [
          {
            title: `<div class="bg-success-500 text-white text-sm font-sans py-2">${this.$t("no")}</div>`,
            handler: () => {
              this.$modal.hide("dialog");
            },
          },
          {
            title: `<div class="bg-red-500 text-white text-sm font-sans py-2">${this.$t("yes")}</div>`,
            handler: async () => {
              this.$modal.hide("dialog");

              // Same as Inaktivera. If it fails, the window stays open with the error.
              const testStopped = await this.resetAlarmTesting();
              if (!testStopped) return;

              this.stopConfirmed = true;
              this.hide("alarm-auto-test");
            },
          },
        ],
      });
    },

    // Back to the start form for a new test. The phone number is kept.
    startOver() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }

      this.timeLeft = null;
      this.duration = this.defaultDuration;
      this.testModeStatus = '';
      this.testingIsActive = false;
      this.testEventRecieved = false;
      this.showErrorMessage = false;
      this.failedStatusChecks = 0;
      this.connectionLost = false;
      this.lastFailReason = '';
      // The customer has just seen how this test ended.
      this.shownResultRunId = this.watchedRunId;
      this.watchedRunId = '';
      this.gettingReadySince = null;
      this.alarmIsSlow = false;

      // The step watcher stops the status checks.
      this.step = 1;
    },

    startInterval() {
      // Clear existing interval to avoid duplicates
      this.clearInterval();

      this.getAlarmTestingStatus();
      // Set up a new interval. Every 3 seconds, so the page follows the alarm's
      // messages ("Test alarm start") closely (SEC-407).
      this.intervalId = setInterval(() => {
        this.getAlarmTestingStatus();
      }, 3000);
    },

    clearInterval() {
      if (this.intervalId) {
        clearInterval(this.intervalId);
        this.intervalId = null;
      }
    },

    startCountdown() {
      if (this.timer) {
        clearInterval(this.timer); // Clear existing timer if any
      }
      let secondsLeft = this.duration;
      this.updateTimeLeft(secondsLeft);

      this.timer = setInterval(() => {
        secondsLeft--;
        this.updateTimeLeft(secondsLeft);

        if (secondsLeft <= 0) {
          clearInterval(this.timer);
          this.timer = null;
          this.timeLeft = null;
        }
      }, 1000);
    },

    updateTimeLeft(seconds) {
      const minutes = Math.floor(seconds / 60);
      const secondsLeft = seconds % 60;
      this.timeLeft = `${minutes}m ${secondsLeft}s`;
    },

    resetAllTimers() {
      if (this.timer) {
        clearInterval(this.timer);
        
      }
      if (this.intervalId) {
        clearInterval(this.intervalId); // Clear the interval on component destroy
      }

      this.timer = null;
      this.intervalId = null;
      this.timeLeft = null;
      this.testingIsActive = false;
      this.testEventRecieved = false;
      this.showErrorMessage = false;
      this.testModeStatus = '';
      this.alarm_mobile = null;
      this.isLoading = false;
      this.failedStatusChecks = 0;
      this.connectionLost = false;
      this.lastFailReason = '';
      // A test this window followed to the end is not "news" when it opens again.
      if (this.watchedRunId) this.shownResultRunId = this.watchedRunId;
      this.watchedRunId = '';
      this.gettingReadySince = null;
      this.alarmIsSlow = false;
    },

    hide(id) {
      this.$modal.hide(id);
    },
  },

  mounted() {
    if (this.step === 2) {
      this.startInterval();
    }
  },

  created() {
    this.modalWidth = window.innerWidth < MODAL_WIDTH ? window.innerWidth : MODAL_WIDTH;
  },
  
  watch: {
    // Watch for changes on 'step'
    step(newVal) {
      if (newVal === 2) {
        this.startInterval();
      } else {
        this.clearInterval();
      }
    },
  },

  computed: {
    countryPrefix() {
      const siteContext = require('@/utils/siteContext');
      return siteContext.isNorwegianSite() ? '+47' : '+46';
    },
    
    testModeStatusMessage() {
      let message = '';

      if(this.testModeStatus == 'tunnel_disable') message = this.$t("alarm_auto_test.tunnel_disable");
      else if(this.testModeStatus == 'device_query_init') message = this.$t("alarm_auto_test.device_query_init");
      else if(this.testModeStatus == 'device_query_success') message = this.$t("alarm_auto_test.device_query_success");
      else if(this.testModeStatus == 'device_test_config') message = this.$t("alarm_auto_test.device_test_config");
      else if(this.testModeStatus == 'recieved_event') message = this.$t("alarm_auto_test.recieved_event");
      else if(this.testStoppedWhileRestoring) message = this.$t("alarm_auto_test.test_stopped");
      else if(this.testModeStatus == 'tunnel_enable') message = this.$t("alarm_auto_test.tunnel_enable");
      else if(this.testStoppedPageLost) message = this.$t("alarm_auto_test.stopped_page_lost");
      else if(this.testModeStatus == 'error' && this.testStoppedBeforeChange) message = this.$t("alarm_auto_test.test_stopped");
      else if(this.testModeStatus == 'error') message = this.$t("alarm_auto_test.error");
      else if(this.testEndedWithoutPress) message = this.$t("alarm_auto_test.done_not_tested");
      else if(this.testModeStatus == 'done') message = this.$t("alarm_auto_test.done");

      return message
    },

    // The test is over, but no test press was received, so the alarm was not
    // tested. Before, this said "Klart" like a finished test (customer test, M1).
    testEndedWithoutPress() {
      return this.testModeStatus == 'done' && !this.testEventRecieved;
    },

    // The alarm is getting ready. It still calls its normal numbers until it has
    // the tester's number, so a press now would call them (customer test, H1).
    isTestGettingReady() {
      if (this.step !== 2) return false;

      const gettingReadyStatuses = ['tunnel_disable', 'device_query_init', 'device_query_success'];
      return gettingReadyStatuses.includes(this.testModeStatus);
    },

    // Opening the window after a test that ended without a press says so once,
    // instead of showing a plain start form (customer test, L8).
    previousTestNote() {
      if (this.step !== 1) return '';
      if (!this.device || this.device.imei_number !== this.alarmInfo.imei_number) return '';

      const lastResult = this.device.test_result;
      if (!lastResult || !lastResult.run_id) return '';
      if (lastResult.run_id === this.shownResultRunId) return '';
      if (lastResult.pressed_at) return '';

      // A cancel was the customer's own choice, and the other reasons can't be
      // fixed from the start form, so only these are worth a note.
      const reasonsWorthANote = [
        'no_press', 'offline', 'no_answer', 'not_ready_in_time', 'rejected', 'page_lost',
        'routes_elsewhere', 'press_not_recognised', 'not_routed_to_securcloud', 'button_disabled', 'config_not_read',
      ];
      if (!reasonsWorthANote.includes(lastResult.fail_reason)) return '';

      const reasonText = this.failReasonText(lastResult.fail_reason);
      return `${this.$t('alarm_auto_test.previous_test_not_done')} ${reasonText}`.trim();
    },

    // The test failed before the alarm got the tester's number. Alarms already go
    // to the alarm centre again (PM decision), so a press now is a real alarm.
    testStoppedBeforeChange() {
      if (this.testModeStatus != 'error') return false;
      if (!this.device || !this.device.test_mode || !this.device.test_mode.testCfg) return false;

      const testSettings = this.device.test_mode.testCfg.config;
      return testSettings.length === 0;
    },

    // The test failed or was cancelled after the alarm got the tester's number,
    // and the alarm is being put back. Alarms already go to the alarm centre
    // again (PM decision D2), so a press now is a real alarm.
    testStoppedWhileRestoring() {
      if (this.testModeStatus != 'tunnel_enable') return false;
      if (!this.device || !this.device.test_mode) return false;

      const failReason = this.device.test_mode.fail_reason;
      return Boolean(failReason);
    },

    // The test has stopped and alarms go to the alarm centre again.
    testHasStopped() {
      return this.testStoppedBeforeChange || this.testStoppedWhileRestoring;
    },

    // A test with a run id that is still going, or whose alarm may still hold the
    // tester's number. The test window shows it even when forwarding is already
    // back on or the 20 minutes are over, so Inaktivera can always be reached.
    testRunNeedsWindow() {
      if (!this.device || !this.device.test_mode || !this.device.test_mode.run_id) return false;

      const activeStatuses = ['tunnel_disable', 'device_query_init', 'device_query_success', 'device_test_config', 'recieved_event', 'tunnel_enable'];
      if (activeStatuses.includes(this.testModeStatus)) return true;

      const needsAttention = this.testModeStatus == 'error' && !this.testStoppedBeforeChange;
      return needsAttention;
    },

    // The server stopped the test because this page stopped checking in (PM decision D1).
    testStoppedPageLost() {
      const testHasEnded = this.testModeStatus == 'error' || this.testModeStatus == 'done';
      if (!testHasEnded) return false;
      if (!this.device || !this.device.test_mode) return false;

      return this.device.test_mode.fail_reason === 'page_lost';
    },

    // Getting ready or waiting for the press: the user is still needed here.
    isTestWaitingForUser() {
      if (this.step !== 2) return false;

      const waitingStatuses = ['tunnel_disable', 'device_query_init', 'device_query_success', 'device_test_config'];
      return waitingStatuses.includes(this.testModeStatus);
    },

    // Why the test stopped, and what the user can do about it.
    stopReasonMessage() {
      const testIsRunning = ['tunnel_disable', 'device_query_init', 'device_query_success', 'device_test_config', 'recieved_event'].includes(this.testModeStatus);
      if (testIsRunning || this.testModeStatus == '') return '';

      return this.failReasonText(this.lastFailReason);
    },

    // What the page is waiting for, and roughly how long it takes.
    waitHintMessage() {
      const status = this.testModeStatus;

      // A TWIG gets ready by SMS and its own hint already says 1-3 minutes.
      if (this.alarmIsSlow && !this.isTwigDevice) return this.$t('alarm_auto_test.wait_hint_slow');
      if (status == 'tunnel_disable') return this.$t('alarm_auto_test.wait_hint_twig_ready');
      if (status == 'device_query_success' && this.isTwigDevice) return this.$t('alarm_auto_test.wait_hint_twig_ready');
      if (status == 'device_query_init' || status == 'device_query_success') return this.$t('alarm_auto_test.wait_hint_srt_ready');
      if (status == 'device_test_config' && this.pressHoldSeconds > 0) {
        return this.$t('alarm_auto_test.press_hold_hint', { seconds: this.pressHoldSeconds });
      }
      if (status == 'recieved_event' && !this.alarmWillCall) return this.$t('alarm_auto_test.wait_hint_after_press_sms');
      if (status == 'recieved_event') return this.$t('alarm_auto_test.wait_hint_after_press');
      if (status == 'tunnel_enable' && !this.testStoppedWhileRestoring) return this.$t('alarm_auto_test.wait_hint_restore');
      // The server sends "Test alarm complete" to an SRT a few seconds after the page says
      // done, also after a test that was not completed. Then explain what it means.
      if (status == 'done' && !this.isTwigDevice && this.testEndedWithoutPress) return this.$t('alarm_auto_test.done_not_tested_device_message');
      if (status == 'done' && !this.isTwigDevice) return this.$t('alarm_auto_test.done_device_message');
      return '';
    },

    // Which of the five steps the user is on. 0 when the test has stopped:
    // then the reason is shown instead of a step.
    currentStepNumber() {
      if (this.step === 1) return 1;

      const status = this.testModeStatus;
      if (['tunnel_disable', 'device_query_init', 'device_query_success'].includes(status)) return 2;
      if (status == 'device_test_config') return 3;
      if (status == 'recieved_event') return 4;

      const testStopped = this.lastFailReason || status == 'error' || this.testHasStopped || this.testStoppedPageLost || this.testEndedWithoutPress;
      if (testStopped) return 0;

      if (status == 'tunnel_enable' || status == 'done') return 5;
      return 0;
    },

    // For example "Steg 2 av 5 · Larmet förbereds".
    stepCounterText() {
      const stepNumber = this.currentStepNumber;
      if (stepNumber === 0) return '';

      let stepName = '';
      if (stepNumber === 1) stepName = this.$t('alarm_auto_test.step_phone');
      if (stepNumber === 2) stepName = this.$t('alarm_auto_test.step_prepare');
      if (stepNumber === 3) stepName = this.$t('alarm_auto_test.step_press');
      if (stepNumber === 4 && this.alarmWillCall) stepName = this.$t('alarm_auto_test.step_call');
      else if (stepNumber === 4) stepName = this.$t('alarm_auto_test.step_sms');
      if (stepNumber === 5 && this.testModeStatus == 'done') stepName = this.$t('alarm_auto_test.step_done');
      else if (stepNumber === 5) stepName = this.$t('alarm_auto_test.step_restore');

      const counter = this.$t('alarm_auto_test.step_counter', { current: stepNumber, total: 5 });
      return `${counter} · ${stepName}`;
    },

    isTwigDevice() {
      if (!this.device || !this.device.type) return false;
      return /^TWIG/.test(this.device.type);
    },

    // What the server decided from the alarm's own set-up before the test, or
    // null before that (and for tests started before the check existed).
    testPlan() {
      if (!this.device || !this.device.test_mode || !this.device.test_mode.plan) return null;
      return this.device.test_mode.plan;
    },

    // Does the alarm call the tester after the press? An SRT whose set-up makes no
    // alarm calls only reaches the tester by the text message from SecurCloud.
    alarmWillCall() {
      if (!this.testPlan || this.testPlan.calls === undefined) return true;
      return Boolean(this.testPlan.calls);
    },

    // Seconds the alarm button has to be held before the alarm counts it (0 = a short press).
    pressHoldSeconds() {
      if (!this.testPlan || !this.testPlan.pressHoldSeconds) return 0;
      return this.testPlan.pressHoldSeconds;
    },

    // The test is over, and a new one can be started from the same window.
    canStartNewTest() {
      if (this.step !== 2) return false;

      return this.testModeStatus == 'done' || this.testStoppedBeforeChange || this.testStoppedPageLost;
    },
  },

  beforeDestroy() {
    this.resetAllTimers();
  },
};
</script>
